import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiltersTypes } from "@common/types/filters/general";
import {
    CloseTemplatesView,
    CloseTemplatesViewColumn,
    CloseTemplatesViewSchema,
} from "@common/types/views/closeTemplatesView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { mergeCachedView } from "@lib/mergeCachedView";
import { HStack } from "@design-system/HStack/HStack";
import { organizationCloseTemplatesViewConfig } from "./organizationCloseTemplatesViewConfig";
import { CreateCloseTemplateDrawer } from "./CreateCloseTemplateDrawer";
import { OrganizationCloseTemplatesList } from "./OrganizationCloseTemplatesList";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import {
    ARCHIVE_FILTER_ITEMS,
    ArchiveFilter,
} from "../templates/archiveFilter";
import { hasActiveListFilters } from "./hasActiveListFilters";

const defaultView: CloseTemplatesView = {
    type: ViewType.CLOSE_TEMPLATES,
    columns: [
        {
            id: CloseTemplatesViewColumn.NAME,
            options: { pin: "left" },
        },
        { id: CloseTemplatesViewColumn.SERVICE },
        { id: CloseTemplatesViewColumn.CLIENT },
        { id: CloseTemplatesViewColumn.BASIS },
        { id: CloseTemplatesViewColumn.OWNER },
        { id: CloseTemplatesViewColumn.NUMBER_OF_SCHEDULES },
        { id: CloseTemplatesViewColumn.NUMBER_OF_APPLICATIONS },
        { id: CloseTemplatesViewColumn.NUMBER_OF_TASKS },
        { id: CloseTemplatesViewColumn.LAST_APPLIED },
        { id: CloseTemplatesViewColumn.UPDATED },
        { id: CloseTemplatesViewColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

export const OrganizationCloseTemplatesPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Present when arriving via the schedules page's "Go to template" action —
    // restricts the list to this one template and defaults the status filter
    // to "all" so archived templates aren't hidden. Mirrored into
    // `filters.templateIds` so it shows as a dismissible active-filter chip
    // (same pattern as transactionIds).
    const templateId = searchParams.get("templateId");

    const [search, setSearch] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>(
        templateId ? "all" : "active",
    );

    // Force the archive filter to "all" when a template scope appears while the
    // page stays mounted (e.g. back/forward into a scoped URL), so archived
    // templates aren't hidden. `useState`'s initial value only runs on mount.
    // Guarded on the truthiness transition so it never clobbers a manual filter
    // change made while the scope is unchanged, and only fires on scope arrival
    // so clearing the scope preserves whatever archive selection the user made.
    const hadTemplateIdRef = useRef(!!templateId);
    useEffect(() => {
        const hasTemplateId = !!templateId;
        if (hasTemplateId === hadTemplateIdRef.current) {
            return;
        }
        hadTemplateIdRef.current = hasTemplateId;
        if (hasTemplateId) {
            setArchiveFilter("all");
        }
    }, [templateId]);

    const [filters, setFilters] = useState<FiltersTypes>(() =>
        templateId ? { templateIds: [templateId] } : {},
    );

    // Keep filters.templateIds aligned with the URL scope so the chip appears
    // / disappears on back/forward and when the chip (or Clear) removes it.
    useEffect(() => {
        setFilters((prev) => {
            const nextTemplateIds = templateId ? [templateId] : undefined;
            const prevId = prev.templateIds?.[0];
            const nextId = nextTemplateIds?.[0];
            if (prevId === nextId) {
                return prev;
            }
            return { ...prev, templateIds: nextTemplateIds };
        });
    }, [templateId]);

    const clearTemplateIdParam = useCallback(() => {
        setSearchParams(
            (params) => {
                params.delete("templateId");
                return params;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const handleFiltersChange = useCallback(
        (partial: Partial<FiltersTypes>) => {
            setFilters((prev) => ({ ...prev, ...partial }));
            if (
                "templateIds" in partial &&
                (!partial.templateIds || partial.templateIds.length === 0)
            ) {
                clearTemplateIdParam();
            }
        },
        [clearTemplateIdParam],
    );

    const handleClearFilters = useCallback(() => {
        setSearch("");
        setArchiveFilter("active");
        setFilters({});
        clearTemplateIdParam();
    }, [clearTemplateIdParam]);

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<CloseTemplatesView>(
            WORKSPACE_USER_CACHE_KEYS.CLOSE_TEMPLATES_PAGE_VIEW_CACHE_KEY,
            CloseTemplatesViewSchema,
        );

    // Re-applies the Name column's `pin: "left"` override (and immutable
    // layout) on top of any stale cached view, so existing users who cached a
    // view before Name became pinned/immutable still get the new layout.
    const [view, setView] = useState<CloseTemplatesView>(() =>
        mergeCachedView(
            cachedView,
            defaultView,
            organizationCloseTemplatesViewConfig,
        ),
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            setView(newView as CloseTemplatesView);
            setCachedView(newView as CloseTemplatesView);
        },
        [setCachedView],
    );

    const hasActiveFilters =
        search !== "" ||
        archiveFilter !== "active" ||
        hasActiveListFilters(filters);

    return (
        <main>
            <TablePageHeader
                search={search}
                onSearchChange={setSearch}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={[
                    FilterTabs.ORG_SERVICE,
                    FilterTabs.TEMPLATE_BASIS,
                    FilterTabs.TEMPLATE_OWNER,
                    FilterTabs.TEMPLATE_LAST_EDITED_BY,
                    FilterTabs.TEMPLATE_TYPE,
                ]}
                isFullPage
            >
                <HStack gap={8} className="ml-auto">
                    <Picker.Select
                        items={ARCHIVE_FILTER_ITEMS}
                        value={archiveFilter}
                        onValueChange={(v) => setArchiveFilter(v ?? "active")}
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={organizationCloseTemplatesViewConfig}
                        defaultView={defaultView}
                        onViewChange={handleViewChange}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyOptions={{
                            description: "Create template",
                            preventDefault: true,
                        }}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => setIsCreateModalOpen(true)}
                        >
                            New template
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>

            <OrganizationCloseTemplatesList
                search={search}
                serviceIds={filters.orgServiceIds}
                ownerIds={filters.closeTemplateOwnerIds}
                noTier={filters.orgServiceNoTier}
                archiveFilter={archiveFilter}
                typeFilter={filters.closeTemplateType}
                selectedBases={filters.closeTemplateBases}
                selectedUpdatedByUserIds={filters.closeTemplateUpdatedByUserIds}
                templateId={templateId}
                view={view}
                onViewChange={handleViewChange}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                onCreateTemplate={() => setIsCreateModalOpen(true)}
            />

            <CreateCloseTemplateDrawer
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </main>
    );
};
